import { useEffect, useState } from 'react'
import { data as remixData, useNavigate, useSearchParams } from 'react-router'
import { IntegralAgropecuarioCoberturasScreen } from '#app/components/cotizacion/screens/integral-agropecuario/IntegralAgropecuarioCoberturasScreen'
import { IntegralAgropecuarioConfirmationScreen } from '#app/components/cotizacion/screens/integral-agropecuario/IntegralAgropecuarioConfirmationScreen'
import { IntegralAgropecuarioIntroScreen } from '#app/components/cotizacion/screens/integral-agropecuario/IntegralAgropecuarioIntroScreen'
import { IntegralAgropecuarioUbicacionScreen } from '#app/components/cotizacion/screens/integral-agropecuario/IntegralAgropecuarioUbicacionScreen'
import { Icon } from '#app/components/ui/icon'
import { Modal } from '#app/components/ui/modal'
import { ProgressBar } from '#app/components/ui/progress-bar'
import { sendCotizacionIntegralAgropecuarioEmail } from '#app/utils/email.server.ts'
import { type Route } from './+types/cotizacion-integral-agropecuario'

export const meta: Route.MetaFunction = () => [
	{ title: 'Cotización Integral Agropecuario - 100prevalor' },
	{
		name: 'description',
		content: 'Cotizá tu seguro Integral Agropecuario con 100prevalor',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const quoteDataString = formData.get('quoteData')

	if (typeof quoteDataString !== 'string') {
		return remixData(
			{ success: false, error: 'Datos inválidos' },
			{ status: 400 },
		)
	}

	const quoteData = JSON.parse(quoteDataString) as IntegralAgropecuarioFormData

	try {
		await sendCotizacionIntegralAgropecuarioEmail(quoteData)
		return remixData({ success: true })
	} catch (error) {
		console.error('Error sending email:', error)
		return remixData(
			{ success: false, error: 'Error al enviar el email' },
			{ status: 500 },
		)
	}
}

interface CoberturaItem {
	enabled: boolean
	sumaAsegurada: string
	adicionales: string[]
}

type IntegralAgropecuarioFormData = {
	// Contacto
	nombreAsegurado: string
	email: string
	telefono: string
	// Ubicación
	ubicacionRiesgo: string
	// Coberturas
	coberturas: {
		incendioCascoEdificio?: CoberturaItem
		incendioCascoContenido?: CoberturaItem
		roboContenidoCasa?: CoberturaItem
		roboValoresCajaFuerte?: CoberturaItem
		incendioInstalaciones?: CoberturaItem
		incendioInsumosDeposito?: CoberturaItem
		incendioImplementosAgricolas?: CoberturaItem
		incendioMercaderias?: CoberturaItem
		incendioTanques?: CoberturaItem
		incendioContenidoTanques?: CoberturaItem
		roboMercaderiasInsumos?: CoberturaItem
		fidelidadEmpleado?: CoberturaItem
		rcComprensiva?: CoberturaItem
		equiposElectronicos?: CoberturaItem
		incendioSilobolsa?: CoberturaItem
	}
	// Adicional
	informacionAdicional?: string
}

export default function CotizacionIntegralAgropecuario() {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const step = parseInt(searchParams.get('step') || '1', 10)

	const [completedSteps, setCompletedSteps] = useState<Set<number>>(
		new Set([1]),
	)
	const [formData, setFormData] = useState<
		Partial<IntegralAgropecuarioFormData>
	>({})

	const totalSteps = 4

	useEffect(() => {
		if (!completedSteps.has(step)) {
			const lastCompleted = Math.max(...Array.from(completedSteps))
			setSearchParams({ step: String(lastCompleted) })
		}
	}, [step, completedSteps, setSearchParams])

	const getProgressPercentage = () => {
		if (step <= 1) return 0
		const progressMap: Record<number, number> = {
			2: 50,
			3: 75,
			4: 100,
		}
		return progressMap[step] || 0
	}

	const currentProgress = getProgressPercentage()
	const showProgressBar = step >= 2

	const handleNext = async (
		stepData?: Partial<IntegralAgropecuarioFormData>,
	) => {
		if (stepData) {
			setFormData({ ...formData, ...stepData })
		}

		if (step === 3) {
			const finalData = { ...formData, ...stepData }

			try {
				const formDataToSend = new FormData()
				formDataToSend.append('quoteData', JSON.stringify(finalData))

				const response = await fetch('/cotizacion-integral-agropecuario', {
					method: 'POST',
					body: formDataToSend,
				})

				if (response.ok) {
					console.log('✅ Email Integral Agropecuario enviado exitosamente')
				} else {
					console.error('❌ Error al enviar email Integral Agropecuario')
				}
			} catch (error) {
				console.error('❌ Error al enviar email Integral Agropecuario:', error)
			}
		}

		if (step < totalSteps) {
			setCompletedSteps((prev) => new Set([...prev, step + 1]))
			setSearchParams({ step: String(step + 1) })
		}
	}

	const handleBack = () => {
		if (step > 1) {
			setSearchParams({ step: String(step - 1) })
		}
	}

	const handleClose = () => {
		void navigate('/seguros')
	}

	const handleRestart = () => {
		setFormData({})
		setCompletedSteps(new Set([1]))
		setSearchParams({ step: '1' })
	}

	const renderStep = () => {
		switch (step) {
			case 1:
				return <IntegralAgropecuarioIntroScreen onNext={() => handleNext()} />
			case 2:
				return (
					<IntegralAgropecuarioUbicacionScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
					/>
				)
			case 3:
				return (
					<IntegralAgropecuarioCoberturasScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
					/>
				)
			case 4:
				return (
					<IntegralAgropecuarioConfirmationScreen
						onBack={handleRestart}
						onClose={handleClose}
						data={formData as IntegralAgropecuarioFormData}
					/>
				)
			default:
				return <IntegralAgropecuarioIntroScreen onNext={() => handleNext()} />
		}
	}

	return (
		<>
			<div className="fixed inset-0 z-50 h-screen bg-neutral-50 md:hidden">
				<div className="flex h-full flex-col">
					<div className="flex shrink-0 items-center justify-end p-4">
						<button
							onClick={handleClose}
							className="flex items-center justify-center rounded-full p-2 transition-colors hover:bg-neutral-200"
							aria-label="Cerrar"
						>
							<Icon name="cross-1" className="size-6 text-neutral-900" />
						</button>
					</div>

					<div className="flex min-h-0 flex-1 flex-col gap-6 px-6 pb-8">
						{showProgressBar && (
							<ProgressBar progress={currentProgress} icon="tractor" />
						)}
						{renderStep()}
					</div>
				</div>
			</div>

			<div className="hidden md:block">
				<Modal
					isOpen={true}
					onClose={handleClose}
					showCloseButton={false}
					rounded={true}
					maxWidth={616}
				>
					<div className="relative flex h-[700px] w-[616px] flex-col rounded-3xl bg-white shadow-[0px_32px_48px_-12px_rgba(217,217,217,1)]">
						<div className="flex min-h-0 flex-1 flex-col gap-8 p-8">
							{showProgressBar && (
								<ProgressBar progress={currentProgress} icon="tractor" />
							)}

							<div className="flex min-h-0 flex-1 flex-col">{renderStep()}</div>
						</div>
					</div>
				</Modal>
			</div>
		</>
	)
}
